import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import {
  C1Dashboard,
  C1Donut,
  C1Empty,
  C1Panel,
  C1StatusList,
  C1Tile,
  type C1StatusRow,
} from '@/components/c1';
import { CategoryBars, TrendLine } from '../charts';
import { useEduCoreLaunchUrl } from '@/hooks/useEduCoreLaunchUrl';
import { useInstitutionTerms } from '@/hooks/useInstitutionTerms';
import { EduCorePaths, type EduCorePath } from '@/lib/educoreLinks';
import type { AcademicOverviewInsights } from '../types';
import { toTrendPoints } from './shared';

/**
 * The pre-executive academic overview, unchanged.
 *
 * This is what an EduCore that has not been upgraded to the executive contract
 * still gets rendered as: four-to-six tiles, two charts, and the snapshot rail.
 * It is kept as its own component rather than woven through the executive panel
 * so the fallback stays exactly the screen that was already reviewed and tested,
 * and deleting it later — once every environment sends the new payload — is one
 * file, not an archaeology dig.
 */
export function LegacyOverviewPanel({
  data,
  locale,
  institutionId,
}: {
  data: AcademicOverviewInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const { vocab } = useInstitutionTerms(institutionId);
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;

  // Narrowed once. `fees` is absent on an older EduCore and present-but-unentitled for a
  // dean, and neither of those may render as a school that has collected nothing.
  const fees = data.fees && data.fees.entitled ? data.fees : null;

  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const money = (value: number) =>
    fees?.currency
      ? new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: fees.currency,
          maximumFractionDigits: 0,
        }).format(value)
      : number(value);

  const todayRate = data.attendanceToday?.rate ?? null;

  // A real comparison, not an invented one: today against the thirty-day mean this panel
  // already plots beneath it. Null whenever either side is missing, which is the ordinary
  // state of a morning before the first register — and null renders as an absence rather
  // than as no change.
  const againstAverage =
    todayRate !== null && data.attendance.rate !== null
      ? Math.round((todayRate - data.attendance.rate) * 10) / 10
      : null;

  const snapshot: C1StatusRow[] = [];
  if (data.programmes !== undefined) {
    snapshot.push({
      id: 'programmes',
      label: t('insights.overview.programmes', vocab),
      value: number(data.programmes),
      href: open(EduCorePaths.directorOverview),
    });
  }
  if (data.departments !== undefined) {
    snapshot.push({
      id: 'departments',
      label: t('insights.overview.departments', vocab),
      value: number(data.departments),
      href: open(EduCorePaths.directorOverview),
    });
  }
  if (data.classes) {
    snapshot.push(
      {
        id: 'classes',
        label: t('insights.overview.activeClasses', vocab),
        value: number(data.classes.active),
        href: open(EduCorePaths.classes),
      },
      {
        id: 'enrolled',
        label: t('insights.overview.enrolled', vocab),
        value: number(data.classes.enrolled),
        href: open(EduCorePaths.students),
      },
    );
  }
  if (data.admissions) {
    snapshot.push(
      {
        id: 'newAdmissions',
        label: t('insights.overview.newAdmissions', vocab),
        value: number(data.admissions.newThisMonth),
        href: open(EduCorePaths.admissions),
      },
      {
        id: 'graduated',
        label: t('insights.overview.graduated', vocab),
        value: number(data.admissions.graduated),
        href: open(EduCorePaths.students),
      },
    );
    // Only when there are any. A permanent "Suspended 0" row trains the reader to skip the
    // line, which is exactly the line that matters on the day it is not zero.
    if (data.admissions.suspended > 0) {
      snapshot.push({
        id: 'suspended',
        label: t('insights.overview.suspended', vocab),
        value: number(data.admissions.suspended),
        tone: 'warning',
        href: open(EduCorePaths.students),
      });
    }
  }

  const aside =
    snapshot.length > 0 || fees || data.atRisk ? (
      <>
        {snapshot.length > 0 && (
          <C1Panel title={t('insights.overview.snapshot')}>
            <C1StatusList items={snapshot} />
          </C1Panel>
        )}

        {data.atRisk && (
          <C1Panel title={t('insights.overview.attentionTitle')}>
            {data.atRisk.count > 0 ? (
              <C1Tile
                label={t('insights.overview.atRisk', { threshold: data.atRisk.threshold, ...vocab })}
                value={number(data.atRisk.count)}
                tone="warning"
                icon={<Icon name="warning" size={16} />}
                href={open(EduCorePaths.attendance)}
              />
            ) : (
              <C1Empty
                compact
                title={t('insights.overview.atRiskNone', {
                  threshold: data.atRisk.threshold,
                  ...vocab,
                })}
              />
            )}
          </C1Panel>
        )}

        {fees && (
          <C1Panel
            title={t('insights.overview.feeTitle')}
            action={
              open(EduCorePaths.fees) ? (
                <a
                  href={open(EduCorePaths.fees)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="c1-tableToggle"
                >
                  {t('insights.openInEducore')}
                </a>
              ) : undefined
            }
          >
            <C1StatusList
              items={[
                {
                  id: 'billed',
                  label: t('insights.overview.billed'),
                  value: money(fees.billed),
                  href: open(EduCorePaths.fees),
                },
                {
                  id: 'collected',
                  label: t('insights.overview.collected'),
                  value: money(fees.collected),
                  href: open(EduCorePaths.fees),
                },
                {
                  id: 'outstanding',
                  label: t('insights.overview.outstanding'),
                  value: money(fees.outstanding),
                  tone: fees.outstanding > 0 ? 'warning' : undefined,
                  href: open(EduCorePaths.fees),
                },
                {
                  id: 'rate',
                  label: t('insights.overview.collectionRate'),
                  // Nothing billed is not a nought per cent collection.
                  value:
                    fees.collectionRate === null
                      ? t('insights.overview.nothingBilled')
                      : `${fees.collectionRate}%`,
                  href: open(EduCorePaths.reports),
                },
              ]}
            />
            <p className="insight-scopeNote">{t('insights.overview.feeCaption')}</p>
          </C1Panel>
        )}
      </>
    ) : null;

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.overview.snapshot')} stickyAside>
      {data.institutionWide && (
        <p className="insight-scopeNote">
          {t('insights.overview.institutionWide')}
          {educore.launchUrl
            ? ` · ${t('insights.clickToOpen')}`
            : ` · ${t('insights.educoreNotLinked')}`}
        </p>
      )}

      <div className="insight-tiles">
        <C1Tile
          label={t('insights.overview.students', vocab)}
          value={number(data.students)}
          icon={<Icon name="users" size={16} />}
          href={open(EduCorePaths.students)}
        />

        {data.staff ? (
          <C1Tile
            label={t('insights.overview.staff', vocab)}
            value={number(data.staff.total)}
            // The active count is a qualifier, never the headline: the flag behind it
            // defaults to inactive, so an imported roster would report a staffed school
            // as empty.
            sub={t('insights.overview.staffActive', { count: data.staff.active })}
            icon={<Icon name="teacher" size={16} />}
            href={open(EduCorePaths.directorOverview)}
          />
        ) : null}

        {data.attendanceToday ? (
          <C1Tile
            label={t('insights.overview.attendanceToday')}
            value={todayRate === null ? '—' : `${todayRate}%`}
            sub={todayRate === null ? t('insights.overview.noRegisterYet') : undefined}
            icon={<Icon name="pulse" size={16} />}
            href={open(EduCorePaths.attendance)}
            trend={{
              delta: againstAverage,
              unit: '%',
              comparison: t('insights.overview.vsAverage'),
              goodWhen: 'up',
            }}
            sparkline={data.attendance.series.map((entry) => entry.rate)}
          />
        ) : (
          <C1Tile
            label={t('insights.overview.attendance')}
            value={data.attendance.rate === null ? '—' : `${data.attendance.rate}%`}
            icon={<Icon name="pulse" size={16} />}
            href={open(EduCorePaths.attendance)}
          />
        )}

        <C1Tile
          label={t('insights.overview.average')}
          value={data.grades.average === null ? '—' : `${data.grades.average}%`}
          icon={<Icon name="academic" size={16} />}
          href={open(EduCorePaths.grades)}
        />

        {/* There is deliberately no "assessments" tile any more. The donut below carries
            that total in its hole, which is the whole reason a donut is allowed here, and
            printing the same number twice on one screen invites a reader to hunt for the
            difference between them. */}
        {fees ? (
          <C1Tile
            label={t('insights.overview.collectedThisMonth')}
            value={money(fees.collectedThisMonth)}
            sub={t('insights.overview.paymentsCount', { count: fees.paymentsThisMonth })}
            icon={<Icon name="report" size={16} />}
            href={open(EduCorePaths.fees)}
          />
        ) : null}
      </div>

      <div className="insight-charts">
        <TrendLine
          title={t('insights.overview.attendanceTrend', vocab)}
          points={toTrendPoints(data.attendance.series, locale)}
          threshold={data.attendance.threshold}
          thresholdLabel={t('insights.thresholdShort', { threshold: data.attendance.threshold })}
        />
        {/* A donut rather than bars for the grade bands, and the two are not
            interchangeable. Bands are parts of one total — every assessment falls in
            exactly one — so the question is what share sits where, and the hole carries the
            total a reader would otherwise have to add up. Sorted, because the ring is drawn
            in a single ordered hue and an unsorted A/D/B/C would make the ramp meaningless. */}
        <C1Donut
          title={t('insights.overview.gradeBands')}
          slices={[...data.grades.distribution]
            .sort((a, b) => a.letter.localeCompare(b.letter))
            .map((entry) => ({ key: entry.letter, label: entry.letter, value: entry.count }))}
          format={number}
          centre={{ value: number(data.grades.assessed), label: t('insights.overview.assessed') }}
        />
        <CategoryBars
          title={t('insights.overview.byDepartment', vocab)}
          data={data.byDepartment.map((entry) => ({
            key: entry.department,
            label: entry.department,
            value: entry.count,
          }))}
          emptyMessage={t('insights.registrar.noDepartments')}
        />
      </div>
    </C1Dashboard>
  );
}
